import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { offCanvasContainerEnum } from '@jooxter/ui/src/components/JxtOffCanvas/types';
import { AddinBookingOffCanvasTabEnum, BookingOffCanvasTabEnum, OffCanvasModeEnum } from '@jooxter/utils';
import { BookingQueryKeys } from '../queries';
import { queryClient } from '../react-query.client';
import { defaultBookingState, defaultFloorState, defaultResourceState } from './offcanvas.constants';
import { OffCanvasKindEnum, OffCanvasStoreState } from './offcanvas.types';

export * from './offcanvas.types';

// This store is the single owner of "what is the off-canvas currently showing" - one `kind` active
// at a time, one branch of state for it. There is no separate render-time config object recomputed
// and pushed back in from a `useEffect`: `OffCanvasHost` reads this state directly during render, so
// there is nothing to race between - opening a panel and setting its data happen in the same
// synchronous `set()` call.
const useOffCanvasStoreBase = create<OffCanvasStoreState>()((set, get) => ({
  isOpen: false,
  kind: null,
  container: offCanvasContainerEnum.Main,
  setContainer: (container) => set((state) => (state.container === container ? {} : { container })),
  // Deliberately leaves `container` untouched: it is not part of "what is open", it is which page
  // is currently mounted saying where the panel should render (see RealTimeStatusMap, the one page
  // that isn't happy with the default) - closing or opening something doesn't change that.
  close: () =>
    set(() => ({
      isOpen: false,
      kind: null,
      booking: defaultBookingState,
      floor: defaultFloorState,
      resource: defaultResourceState,
      workplaceCalendar: null,
    })),

  booking: defaultBookingState,
  openBookingView: (bookingId) =>
    set(() => ({
      kind: OffCanvasKindEnum.Booking,
      isOpen: true,
      booking: { ...defaultBookingState, bookingId, mode: OffCanvasModeEnum.View, directEdit: false },
    })),
  setBookingEditHandler: (handler) => set(() => ({ bookingEditHandler: handler })),
  openBookingForEdit: (bookingId) => {
    const handler = get().bookingEditHandler;

    if (handler) {
      get().close();
      handler(bookingId);
      return;
    }

    queryClient.invalidateQueries({ queryKey: [BookingQueryKeys.GetBooking, bookingId] });
    set(() => ({
      kind: OffCanvasKindEnum.Booking,
      isOpen: true,
      booking: {
        ...defaultBookingState,
        bookingId,
        mode: OffCanvasModeEnum.Update,
        directEdit: true,
        selectedIndex: BookingOffCanvasTabEnum.Form,
      },
    }));
  },
  bookingGoToEditView: () => {
    const handler = get().bookingEditHandler;
    const editedId = get().booking.bookingId;

    if (handler && editedId) {
      get().close();
      handler(editedId);
      return;
    }

    queryClient.invalidateQueries({ queryKey: [BookingQueryKeys.GetBooking, get().booking.bookingId] });
    set((state) => ({
      booking: { ...state.booking, mode: OffCanvasModeEnum.Update, selectedIndex: BookingOffCanvasTabEnum.Form },
    }));
  },
  goToResourceCalendarView: (resourceId, preset) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Booking,
      isOpen: true,
      booking:
        resourceId && preset
          ? {
              ...state.booking,
              bookingId: undefined,
              resourceId,
              preset,
              mode: OffCanvasModeEnum.Create,
              directEdit: true,
              selectedIndex: BookingOffCanvasTabEnum.Planning,
              previousView: undefined,
              planningDate: undefined,
            }
          : { ...state.booking, selectedIndex: BookingOffCanvasTabEnum.Planning },
    })),
  goToCreateBooking: (resourceId, preset, autoSelectFirstSlot) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Booking,
      isOpen: true,
      booking: {
        ...state.booking,
        bookingId: undefined,
        resourceId,
        preset,
        autoSelectFirstSlot,
        mode: OffCanvasModeEnum.Create,
        directEdit: true,
        selectedIndex: BookingOffCanvasTabEnum.Form,
        previousView: undefined,
        planningDate: undefined,
      },
    })),
  goToResourceInformations: (resourceId, preset) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Booking,
      isOpen: true,
      booking: {
        ...state.booking,
        ...(resourceId && preset
          ? {
              bookingId: undefined,
              resourceId,
              preset,
              mode: OffCanvasModeEnum.Create,
              directEdit: true,
              previousView: undefined,
              planningDate: undefined,
            }
          : {}),
        selectedIndex: BookingOffCanvasTabEnum.Information,
        addinSelectedIndex: AddinBookingOffCanvasTabEnum.Information,
      },
    })),
  goToResourcePlan: () =>
    set((state) => ({
      booking: {
        ...state.booking,
        mode: OffCanvasModeEnum.Create,
        addinSelectedIndex: AddinBookingOffCanvasTabEnum.Plan,
      },
    })),
  // Clicking a booking on the planning tab swaps the panel to its details: remember where we came
  // from so the back arrow can restore it (same booking/mode/tab, same planning date).
  onBookingEventClick: (bookingId, planningDate) =>
    set((state) => ({
      booking: {
        ...state.booking,
        previousView: {
          bookingId: state.booking.bookingId,
          mode: state.booking.mode,
          selectedIndex: state.booking.selectedIndex,
          planningDate,
        },
        bookingId,
        mode: OffCanvasModeEnum.View,
      },
    })),
  bookingGoBack: () =>
    set((state) => {
      const previousView = state.booking.previousView;
      if (!previousView) {
        return {};
      }
      return {
        booking: {
          ...state.booking,
          bookingId: previousView.bookingId,
          mode: previousView.mode,
          selectedIndex: previousView.selectedIndex,
          planningDate: previousView.planningDate,
          previousView: undefined,
        },
      };
    }),
  // The user just dragged an explicit slot on the timeline: that pick must win, so force
  // `autoSelectFirstSlot` back to false here rather than leaving whatever a previous flow left in
  // the store (the store is a singleton now, it no longer resets itself between opens - see the
  // comment atop useBookingOffCanvasContent). Otherwise a stale `true` silently lets
  // JxtSmartSlotSelectorContainer auto-pick the first morning/afternoon/evening slot on mount and
  // overwrite the hours the user just chose.
  onBookingCalendarSelect: (preset, resourceId) =>
    set((state) => ({
      booking: {
        ...state.booking,
        preset,
        resourceId,
        mode: OffCanvasModeEnum.Create,
        selectedIndex: BookingOffCanvasTabEnum.Form,
        autoSelectFirstSlot: false,
      },
    })),
  onBookingSlotSelect: (slot, preset, resourceId) =>
    set((state) => ({
      booking: {
        ...state.booking,
        resourceId,
        preset,
        slotToBookInstantly: slot,
        mode: OffCanvasModeEnum.Create,
        selectedIndex: BookingOffCanvasTabEnum.Form,
        autoSelectFirstSlot: false,
      },
    })),
  // These three are the ones an effect can call on every render of its owner (see the
  // "not bookable -> fall back to the Information tab" effect in useBookingOffCanvasContent): a
  // plain `useState` setter silently no-ops when given a value it already holds, but a naive
  // `set((state) => ({ booking: { ...state.booking, x } }))` always allocates a new `booking`
  // object and always notifies - which re-renders the very component whose effect just fired,
  // recomputing the same "not bookable" condition and firing it again. Returning `{}` (an update
  // with no keys) when the value is already correct keeps `state.booking`'s reference untouched, so
  // a selector on `state.booking` sees no change and effects depending on it don't re-fire.
  setBookingMode: (mode) =>
    set((state) => (state.booking.mode === mode ? {} : { booking: { ...state.booking, mode } })),
  setBookingSelectedIndex: (selectedIndex) =>
    set((state) =>
      state.booking.selectedIndex === selectedIndex ? {} : { booking: { ...state.booking, selectedIndex } }
    ),
  setAddinBookingSelectedIndex: (addinSelectedIndex) =>
    set((state) =>
      state.booking.addinSelectedIndex === addinSelectedIndex
        ? {}
        : { booking: { ...state.booking, addinSelectedIndex } }
    ),

  floor: defaultFloorState,
  openFloorView: (id, floorId, selectedBuilding) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Floor,
      isOpen: true,
      floor: {
        ...state.floor,
        floorExceptionalClosingHoursId: id,
        floorId,
        mode: OffCanvasModeEnum.View,
        selectedBuilding: selectedBuilding ?? state.floor.selectedBuilding,
      },
    })),
  openFloorCreate: (floorId, preset, selectedBuilding) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Floor,
      isOpen: true,
      floor: {
        ...state.floor,
        floorExceptionalClosingHoursId: undefined,
        floorId,
        preset,
        mode: OffCanvasModeEnum.Create,
        selectedBuilding: selectedBuilding ?? state.floor.selectedBuilding,
      },
    })),
  floorGoToEditView: () => set((state) => ({ floor: { ...state.floor, mode: OffCanvasModeEnum.Update } })),
  setFloorMode: (mode) => set((state) => (state.floor.mode === mode ? {} : { floor: { ...state.floor, mode } })),

  resource: defaultResourceState,
  openResourceView: (id, resourceId) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Resource,
      isOpen: true,
      resource: { ...state.resource, exceptionalOpeningHoursId: id, resourceId, mode: OffCanvasModeEnum.View },
    })),
  openResourceCreate: (resourceId, preset) =>
    set((state) => ({
      kind: OffCanvasKindEnum.Resource,
      isOpen: true,
      resource: {
        ...state.resource,
        exceptionalOpeningHoursId: undefined,
        resourceId,
        preset,
        mode: OffCanvasModeEnum.Create,
      },
    })),
  resourceGoToEditView: () => set((state) => ({ resource: { ...state.resource, mode: OffCanvasModeEnum.Update } })),
  setResourceMode: (mode) =>
    set((state) => (state.resource.mode === mode ? {} : { resource: { ...state.resource, mode } })),

  workplaceCalendar: null,
  openWorkplaceCalendar: (payload) =>
    set(() => ({ kind: OffCanvasKindEnum.WorkplaceCalendar, isOpen: true, workplaceCalendar: payload })),
}));

// Thin wrapper so callers write `useOffCanvasStore((state) => ({ a: state.a, b: state.b }))` the
// same way `useStore` (packages/core/src/store/useStore.ts) lets callers write array/object
// selectors - without every call site importing `useShallow` and remembering to wrap its own
// selector.
export function useOffCanvasStore<U>(selector: (state: OffCanvasStoreState) => U): U {
  return useOffCanvasStoreBase(useShallow(selector));
}
